import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
// services
import { MessageService } from '../../../../services/message.service'
import { MasterService } from '../../../../services/master.service'
// external
import * as languageLibrary from '../../../../services/language'
import { ActivatedRoute } from '@angular/router';
import { SUPPORTED_IMAGE_TYPES, SUPPORTED_IMAGE_EXTENSIONS } from 'src/app/constants/file';

@Component({
    selector: 'inventory-images-list',
    templateUrl: './inventory-images-list.component.html',
    styleUrls: ['./inventory-images-list.component.scss'],
    standalone: false
})
export class InventoryImagesListComponent implements OnInit {
    /*
    * Variables
    */

    // define the inventoryId
    @Input() inventoryId: number = 0

    // save the language
    @Input() language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // Emit to the parent
    @Output() emitterInformation$: EventEmitter<Object> = new EventEmitter()

    // set all words
    protected words = languageLibrary.language

    // images array
    protected images = []

    protected files = []

    protected imagesToUpload = []

    //  define the image selected
    protected imageSelected: Object = {}

    // define expanded
    protected expanded: boolean = true

    // loading state
    protected loading: boolean = false

    protected selectedId: number = 0;

    // Group of inputs
    protected information: FormGroup;

    protected showUploadButton: boolean = false;

    protected allFiles: Array<Object> = []

    get caption() { return this.information.get('caption') }
    get description() { return this.information.get('description') }
    get imageList() {
        return this.information.controls['imageList'] as FormArray;
    }
    get extensions() { return SUPPORTED_IMAGE_EXTENSIONS.join(',') }

    /*
    * functions
    */

    public async sort() {
        if (!this.images || (this.images.length <= 0)) {
            return
        }

        const ids = this.images.map(image => image['id'])

        this.master.patch(`inventory/${this.inventoryId}/upload`, { information: ids }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return;
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
            }

            this.getImages()
        })
    }

    public drop(event: CdkDragDrop<string[]>) {
        if (event.previousIndex == event.currentIndex) {
            return
        }
        moveItemInArray(this.images, event.previousIndex, event.currentIndex);
        this.sort()
    }

    //  cut base64 string
    public subStringB64 = (word: string): string => {
        let index = word.indexOf(',')
        return word.substring(index + 1)
    }
    // resize the base64 image
    public async resizeImage(image, maxWidth, maxHeight) {
        return new Promise((resolve, reject) => {
            let img = new Image()
            img.onload = () => {
                let canvas = (document.createElement('canvas') as HTMLCanvasElement)
                // size
                const MAX_WIDTH = maxWidth
                const MAX_HEIGHT = maxHeight
                let width = img.width
                let height = img.height

                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width
                        width = MAX_WIDTH
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width *= MAX_HEIGHT / height
                        height = MAX_HEIGHT
                    }
                }
                canvas.width = width
                canvas.height = height
                let cx = canvas.getContext('2d')

                cx.drawImage(img, 0, 0, width, height)
                resolve(this.subStringB64(canvas.toDataURL()))
            }
            img.onerror = reject
            img.src = image
        })
    }
    /*
    * life cycles
    */
    constructor(private ms: MessageService, public master: MasterService, public route: ActivatedRoute, private fb: FormBuilder) {
        this.information = this.fb.group({
            imageList: this.fb.array([])
        })
    }

    ngOnInit() {
        if (this.route.snapshot.params["id"] === 'add') {
            return
        }

        this.getImages()
    }

    public setDefaultImage(fileURL: string) {
        this.emitterInformation$.emit({ message: 'setDefaultImage', fileURL })
    }

    public addImages(images) {
        this.imageList.clear()
        for (let i = 0; i < images.length; i++) {
            if (images[i].sortOrder == 1) {
                this.setDefaultImage(images[i].url)
            }

            this.imageList.push(this.fb.group({
                id: [images[i].id, [Validators.required]],
                caption: [images[i].caption],
                description: [images[i].description],
                url: [images[i].url],
                markForDeletion: false
            }))
        }
    }

    public getImages() {
        this.master.get(`inventory/${this.inventoryId}/upload`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.images = res.data.uploads
            this.resetInformation()
        })
    }

    public createImages(imageData: any) {
        if (imageData.length == 0 || this.loading == true) {
            return
        }

        this.loading = true
        this.master.post(`inventory/${this.inventoryId}/upload`, { information: imageData }, res => {
            this.loading = false
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            const btn = (document.getElementById('btn-close-modal-upload-image') as HTMLButtonElement)
            if (btn) {
                btn.click()
            }

            this.showUploadButton = false
            this.getImages()
        })
    }

    public modifyImage() {
        this.setDefaultImage('')
        const modifiedImages = this.information.controls['imageList']['controls']
            .filter(image => image.pristine == false
            ).map(image => { return { id: image.value.id, caption: image.value.caption, description: image.value.description, markForDeletion: image.value.markForDeletion } })

        const btn = (document.getElementById('btn-close-modal-modify-image') as HTMLButtonElement)
        if (btn) { btn.click() }

        if (modifiedImages.length == 0) {
            return
        }

        this.master.put(`inventory/${this.inventoryId}/upload`, { information: modifiedImages }, res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            const deleted = modifiedImages.filter((image) => image.markForDeletion == 'true')
            for (const marked in deleted) {
                const index = this.images.findIndex((image) => image.id == marked['id'])
                if (index >= 0) {
                    this.images.splice(index, 1)
                }
            }

            this.sort()
        })
    }

    public setId(id: number) {
        this.selectedId = id
    }

    public deleteImage() {
        this.setDefaultImage('')

        const btn = (document.getElementById('btn-close-modal-delete-image') as HTMLButtonElement)
        if (btn) {
            btn.click()
        }

        this.master.discard(`inventory/${this.inventoryId}/upload/${this.selectedId}`, res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            const index = this.images.findIndex((image) => image.id == this.selectedId)
            if (index >= 0) {
                this.images.splice(index, 1)
            }

            this.sort()
        })
    }

    // get image and convert to base64
    public async uploadImages(e) {
        const context = this
        let counter = this.images.length > 0 ? this.images.length + 1 : 1

        this.files = e.target.files
        for (const file of this.files) {
            // check if file is supported
            if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
                this.ms.sendMessage("alert", { type: "danger", text: `${file.name} is not a supported image type.` });
                continue
            }

            this.allFiles.push(file)

            // * format valid
            const reader = new FileReader()
            reader.onload = async function (event) {
                context.imagesToUpload.push({
                    'name': file.name,
                    'caption': file.name,
                    'fileSize': file.size,
                    'fileType': file.type,
                    'module': "Inventory",
                    'sortOrder': counter++,
                    'inventoryId': context.inventoryId,
                    '1024': await context.resizeImage(reader.result, 1024, 768),
                    '480': await context.resizeImage(reader.result, 480, 360),
                    'sizeThumb': 480,
                    'url': event.target.result,
                })

                context.showUploadButton = true
            }

            reader.readAsDataURL(file)
        }
    }

    public deleteImageToUpload(i) {
        this.imagesToUpload.splice(i, 1)
    }

    // select a image
    public resetInformation() {
        this.information.reset()
        this.allFiles = []
        this.addImages(this.images)
        this.imageSelected = null
        this.selectedId = null
        this.imagesToUpload = []
        this.files = []
    }
}
